<template>
  <div class="flow-panel" dir="rtl">
    <div class="flow-head">
      <b-icon icon="share" class="flow-head-icon"/>
      <span>مسیر مراحل</span>
      <small>از راست به چپ خوانده می‌شود</small>
    </div>

    <div v-if="!levels.length" class="flow-empty">
      هنوز مرحله‌ای برای نمایش مسیر نیست.
    </div>

    <div v-else class="flow-body">
      <div v-for="(level, levelIndex) in levels" :key="`lv-${levelIndex}`" class="flow-level">
        <div
          v-if="levelIndex > 0"
          class="flow-join"
          :class="joinClass(levels[levelIndex - 1].length, level.length)"
        >
          <div class="join-stem"></div>
          <div v-if="levels[levelIndex - 1].length === 1 && level.length > 1" class="join-bar"></div>
          <div v-if="levels[levelIndex - 1].length > 1 && level.length === 1" class="join-bar"></div>
        </div>

        <div class="flow-row" :class="{ 'is-branch': level.length > 1 }">
          <div v-for="(node, nodeIndex) in level" :key="node.id" class="flow-col">
            <div
              class="flow-card"
              :class="[cardClass(levelIndex, nodeIndex, level.length, node), { selected: isSelected(node) }]"
              @click="$emit('select-step', node.step)"
            >
              <span class="flow-num">{{ toPersian(node.number) }}</span>
              <span class="flow-title">{{ node.title }}</span>
              <span v-if="node.badge" class="flow-badge" :class="badgeClass(nodeIndex, level.length)">
                شرط: {{ node.badge }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flow-foot">
      <b-icon icon="eye-slash"/>
      <span>استپ مخفی با visible_when دیده نمی‌شود.</span>
    </div>
  </div>
</template>

<script>
const BRANCH_COLORS = ['green', 'orange', 'blue', 'purple']

export default {
  name: "FormFlowChart",
  props: {
    questions: {
      type: Array,
      default: () => []
    },
    selectedId: {
      default: null
    }
  },
  computed: {
    visibleQuestions() {
      return (this.questions || []).filter(question => question.visibility !== false)
    },
    steps() {
      return this.visibleQuestions.filter(question => question.type === 'step' && !question.visible_when)
    },
    levels() {
      if (!this.steps.length) {
        return []
      }
      const byId = {}
      this.steps.forEach(step => {
        byId[String(step.id)] = step
      })
      const visited = {}
      const incoming = {}
      const levels = []
      let current = [this.steps[0]]

      while (current.length) {
        const nodes = []
        current.forEach(step => {
          const key = String(step.id)
          if (!step || visited[key]) {
            return
          }
          visited[key] = true
          nodes.push(this.makeNode(step, incoming[key] || ''))
        })
        if (!nodes.length) {
          break
        }
        levels.push(nodes)

        const next = []
        const seenNext = {}
        nodes.forEach(node => {
          this.outgoing(node.step).forEach(edge => {
            const key = String(edge.to)
            if (incoming[key] === undefined) {
              incoming[key] = edge.badge
            } else if (edge.badge && incoming[key] && incoming[key] !== edge.badge) {
              incoming[key] = `${incoming[key]} / ${edge.badge}`
            } else if (edge.badge && !incoming[key]) {
              incoming[key] = edge.badge
            }
            if (!visited[key] && !seenNext[key] && byId[key]) {
              seenNext[key] = true
              next.push(byId[key])
            }
          })
        })
        current = next
      }

      this.steps.forEach(step => {
        if (!visited[String(step.id)]) {
          levels.push([this.makeNode(step, '')])
        }
      })
      return levels
    }
  },
  methods: {
    isSelected(node) {
      return this.selectedId !== null && String(this.selectedId) === String(node.id)
    },
    makeNode(step, badge) {
      return {
        id: step.id,
        step,
        title: step.title || 'بدون عنوان',
        number: this.steps.findIndex(item => String(item.id) === String(step.id)) + 1,
        badge
      }
    },
    sequentialNext(step) {
      const index = this.steps.findIndex(item => String(item.id) === String(step.id))
      const next = this.steps[index + 1]
      return next ? next.id : ''
    },
    outgoing(step) {
      const next = step.config && step.config.next ? step.config.next : {default: '', rules: []}
      const edges = []
      const seen = {}
      ;(next.rules || []).forEach(rule => {
        if (rule.next === '' || rule.next === null || typeof rule.next === 'undefined') {
          return
        }
        const key = String(rule.next)
        const badge = this.shortBadge(rule)
        if (seen[key]) {
          const existing = edges.find(item => String(item.to) === key)
          if (badge && existing.badge) {
            existing.badge = `${existing.badge} / ${badge}`
          } else if (badge) {
            existing.badge = badge
          }
          return
        }
        seen[key] = true
        edges.push({to: rule.next, badge})
      })
      const fallback = next.default
      if (fallback !== '' && fallback !== null && typeof fallback !== 'undefined' && !seen[String(fallback)]) {
        edges.push({to: fallback, badge: edges.length ? 'سایر' : ''})
      }
      if (!edges.length) {
        const sequential = this.sequentialNext(step)
        if (sequential) {
          edges.push({to: sequential, badge: ''})
        }
      }
      return edges
    },
    shortBadge(rule) {
      const when = rule.when || {}
      const conditions = when.any || when.all || []
      if (!conditions.length) {
        return rule.id || ''
      }
      if (conditions.length === 1) {
        return this.conditionValue(conditions[0])
      }
      return conditions.map(item => this.conditionValue(item)).filter(Boolean).join(' و ')
    },
    conditionValue(condition) {
      if (condition.op === 'between' && Array.isArray(condition.value)) {
        return `${condition.value[0]} تا ${condition.value[1]}`
      }
      const label = this.optionLabel(condition.question_id, condition.value)
      return label === undefined || label === null || label === '' ? '' : String(label)
    },
    optionLabel(questionId, value) {
      const question = this.visibleQuestions.find(item => String(item.id) === String(questionId))
      if (question && Array.isArray(question.options)) {
        const option = question.options.find(item => String(item.id) === String(value) || String(item.value) === String(value))
        if (option) {
          return option.label || value
        }
      }
      return value
    },
    toPersian(value) {
      return String(value).replace(/\d/g, digit => '۰۱۲۳۴۵۶۷۸۹'[digit])
    },
    joinClass(prevCount, nextCount) {
      if (prevCount === 1 && nextCount > 1) {
        return 'is-fork'
      }
      if (prevCount > 1 && nextCount === 1) {
        return 'is-merge'
      }
      return 'is-line'
    },
    cardClass(levelIndex, nodeIndex, levelSize, node) {
      if (levelIndex === 0) {
        return 'is-start'
      }
      if (levelSize > 1) {
        return `is-branch is-${BRANCH_COLORS[nodeIndex % BRANCH_COLORS.length]}`
      }
      return 'is-plain'
    },
    badgeClass(nodeIndex, levelSize) {
      if (levelSize < 2) {
        return 'badge-green'
      }
      return `badge-${BRANCH_COLORS[nodeIndex % BRANCH_COLORS.length]}`
    }
  }
}
</script>

<style scoped>
.flow-panel {
  width: 100%;
  background: #eef1f4;
  border-radius: 16px;
  padding: 14px 14px 12px;
}

.flow-head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 12px;
}

.flow-head small {
  font-size: 11px;
  color: #9ca3af;
}

.flow-head-icon {
  transform: rotate(90deg);
}

.flow-empty {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  color: #6b7280;
  font-size: 13px;
}

.flow-body {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0;
  width: 100%;
  overflow-x: auto;
  padding: 8px 4px 12px;
  min-height: 120px;
}

.flow-level {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1 1 0;
  min-width: 148px;
}

.flow-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  width: 100%;
}

.flow-col {
  min-width: 0;
  width: 100%;
}

.flow-row.is-branch .flow-col {
  max-width: none;
}

.flow-card.selected {
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.25);
}

.flow-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  min-height: 80px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.flow-card.is-start {
  border-color: #14b8a6;
}

.flow-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  margin-bottom: 6px;
  background: #9ca3af;
}

.flow-card.is-start .flow-num {
  background: #0f766e;
}

.flow-card.is-green .flow-num {
  background: #16a34a;
}

.flow-card.is-orange .flow-num {
  background: #f59e0b;
}

.flow-card.is-blue .flow-num {
  background: #3b82f6;
}

.flow-card.is-purple .flow-num {
  background: #8b5cf6;
}

.flow-title {
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}

.flow-badge {
  margin-top: 8px;
  display: inline-block;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
}

.badge-green {
  background: #dcfce7;
  color: #15803d;
}

.badge-orange {
  background: #ffedd5;
  color: #c2410c;
}

.badge-blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-purple {
  background: #ede9fe;
  color: #6d28d9;
}

.flow-join {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0 4px;
}

.join-stem {
  width: 32px;
  height: 2px;
  background: #c5cad3;
  position: relative;
}

.join-stem:after {
  content: "";
  position: absolute;
  left: -1px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-right: 6px solid #c5cad3;
}

.flow-join.is-fork .join-stem:after,
.flow-join.is-merge .join-stem:after {
  display: none;
}

.join-bar {
  width: 2px;
  height: 56px;
  background: #c5cad3;
}

.flow-join.is-fork .join-stem,
.flow-join.is-merge .join-stem {
  width: 18px;
}

.flow-join.is-merge .join-stem:after {
  display: block;
}

.flow-foot {
  margin-top: 8px;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
